import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Loader2,
  ArrowLeft,
  Check,
  X,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

export default function DeckViewer() {
  const { deckId } = useParams();
  const nav = useNavigate();
  const [deck, setDeck] = useState(null);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [sessionScores, setSessionScores] = useState({}); // {idx: "right"|"wrong"}

  useEffect(() => {
    api.get(`/custom-flashcards/${deckId}`)
      .then((r) => setDeck(r.data))
      .catch(() => setDeck({ error: true }));
  }, [deckId]);

  if (!deck) {
    return (
      <div className="text-sm py-10 text-center" style={{ color: "var(--text-muted)" }}>
        <Loader2 className="mx-auto animate-spin mb-2" size={20} />
        Loading deck…
      </div>
    );
  }
  if (deck.error) {
    return (
      <div className="pm-card p-8 text-center">
        <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
          Deck not found. It may have been deleted.
        </p>
        <Link to="/app/decks" className="pm-btn-secondary">Back to My Decks</Link>
      </div>
    );
  }

  const cards = deck.cards || [];
  const current = cards[idx];
  const total = cards.length;
  const scoredThisSession = Object.keys(sessionScores).length;

  const grade = async (correct) => {
    setSessionScores((s) => ({ ...s, [idx]: correct ? "right" : "wrong" }));
    // Fire-and-forget backend increment
    api.post(`/custom-flashcards/${deckId}/score`, {
      card_index: idx,
      correct,
    }).catch(() => {});
    setTimeout(() => {
      setFlipped(false);
      if (idx < total - 1) {
        setIdx(idx + 1);
      } else {
        toast.success(`Session complete — ${scoredThisSession + 1}/${total} cards drilled`);
      }
    }, 350);
  };

  const restart = () => {
    setIdx(0);
    setFlipped(false);
    setSessionScores({});
  };

  const remove = async () => {
    if (!window.confirm("Delete this deck? Your session scores will be lost.")) return;
    try {
      await api.delete(`/custom-flashcards/${deckId}`);
      toast.success("Deck deleted");
      nav("/app/decks");
    } catch { toast.error("Couldn't delete deck"); }
  };

  const rightCount = Object.values(sessionScores).filter((v) => v === "right").length;
  const wrongCount = Object.values(sessionScores).filter((v) => v === "wrong").length;

  return (
    <div className="max-w-3xl mx-auto" data-testid="deck-viewer">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Link to="/app/decks" className="text-sm underline inline-flex items-center gap-1" style={{ color: "var(--primary)" }} data-testid="deck-back-link">
          <ArrowLeft size={12} /> All decks
        </Link>
        <button onClick={restart} className="text-sm inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }} data-testid="deck-restart">
          <RotateCcw size={12} /> Restart
        </button>
        <button onClick={remove} className="text-sm inline-flex items-center gap-1 ml-auto" style={{ color: "var(--weakness)" }} data-testid="deck-delete">
          <Trash2 size={12} /> Delete deck
        </button>
      </div>

      <div className="mb-4">
        <h1 className="font-display text-3xl font-bold mb-1" data-testid="deck-title">{deck.title}</h1>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          Card {idx + 1} of {total} · <span style={{ color: "var(--mastery)" }}>{rightCount} right</span> · <span style={{ color: "var(--weakness)" }}>{wrongCount} wrong</span>
        </p>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full overflow-hidden mb-6" style={{ background: "var(--muted)" }}>
        <div
          className="h-full transition-all"
          style={{ width: `${(scoredThisSession / total) * 100}%`, background: "var(--primary)" }}
        />
      </div>

      {current ? (
        <div
          onClick={() => setFlipped((f) => !f)}
          className="pm-card p-8 min-h-[280px] flex items-center justify-center text-center cursor-pointer transition-transform hover:-translate-y-0.5"
          style={{ background: flipped ? "var(--hl-yellow)" : "var(--paper)" }}
          data-testid="deck-card"
        >
          <div>
            <div className="text-[10px] uppercase font-bold tracking-widest mb-3" style={{ color: "var(--text-muted)" }}>
              {flipped ? "Answer" : "Front — tap to flip"}
            </div>
            <div className="text-xl leading-relaxed" data-testid={flipped ? "deck-card-back" : "deck-card-front"}>
              {flipped ? current.back : current.front}
            </div>
          </div>
        </div>
      ) : (
        <div className="pm-card p-8 text-center">
          <p className="mb-3">Nice work — you finished this deck!</p>
          <button onClick={restart} className="pm-btn-primary">Drill again</button>
        </div>
      )}

      {current && flipped && (
        <div className="mt-5 grid grid-cols-2 gap-3" data-testid="deck-grade-row">
          <button
            onClick={() => grade(false)}
            className="pm-btn-secondary flex items-center justify-center gap-2 py-4"
            style={{ borderColor: "var(--weakness)", color: "var(--weakness)" }}
            data-testid="deck-grade-wrong"
          >
            <X size={16} /> Didn't know
          </button>
          <button
            onClick={() => grade(true)}
            className="pm-btn-primary flex items-center justify-center gap-2 py-4"
            style={{ background: "var(--mastery)" }}
            data-testid="deck-grade-right"
          >
            <Check size={16} /> Got it
          </button>
        </div>
      )}
    </div>
  );
}
